import { useState } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface PermanentDeleteDialogProps {
  eventName: string | null;
  busy: boolean;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
}

/**
 * JUB-157: permanent delete from the Trash. The owner types the event name
 * to confirm; the purchase stays consumed and is never reusable.
 */
export function PermanentDeleteDialog({ eventName, busy, onConfirm, onOpenChange }: PermanentDeleteDialogProps) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState("");
  const matches = eventName !== null && typed.trim() === eventName.trim();

  return (
    <AlertDialog
      open={eventName !== null}
      onOpenChange={(open) => {
        if (!open) setTyped("");
        onOpenChange(open);
      }}
    >
      <AlertDialogContent data-testid="permanent-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{t("events.trash.permanentTitle")}</AlertDialogTitle>
          <AlertDialogDescription>{t("events.trash.permanentDescription")}</AlertDialogDescription>
        </AlertDialogHeader>
        <label className="text-sm" htmlFor="permanent-delete-name">
          {t("events.trash.typeNameLabel", { name: eventName ?? "" })}
        </label>
        <Input
          id="permanent-delete-name"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoComplete="off"
          data-testid="permanent-delete-name-input"
        />
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              if (!matches) {
                e.preventDefault();
                return;
              }
              onConfirm();
            }}
            disabled={!matches || busy}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid="confirm-permanent-delete-button"
          >
            {busy ? t("common.loading") : t("events.trash.deletePermanently")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
